/**
 * AgentPaymentLinks — agent/admin creates shareable payment links.
 * Customer pays via /pay/{code} → treasury credits → commission auto-splits to agent wallet.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Copy, Plus, ExternalLink, CheckCircle2, Circle, Link2, Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

function money(n) { return `£${(Number(n) || 0).toFixed(2)}`; }

export default function AgentPaymentLinks() {
  const [links, setLinks] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ amount_gbp: "", description: "", customer_email: "", customer_name: "", commission_pct: 10 });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/agent/payment-links");
      setLinks(data.payment_links || []);
    } catch (e) { toast.error("Failed to load payment links"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount_gbp);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    if (!form.description || form.description.length < 3) return toast.error("Description required");
    setCreating(true);
    try {
      const { data } = await api.post("/agent/payment-links", {
        ...form,
        amount_gbp: amount,
        commission_pct: parseFloat(form.commission_pct) || 10,
      });
      toast.success("Payment link created");
      setShowCreate(false);
      setForm({ amount_gbp: "", description: "", customer_email: "", customer_name: "", commission_pct: 10 });
      load();
      // Auto-copy
      copyLink(data.code);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setCreating(false);
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/pay/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const uploadCsv = async (e) => {
    e.preventDefault();
    if (!csvFile) return toast.error("Choose a CSV file first");
    setUploading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const { data } = await api.post("/agent/payment-links/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      toast.success(`Created ${data.created} links${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upload failed");
    }
    setUploading(false);
  };

  const downloadCsvTemplate = () => {
    const csv = "amount_gbp,description,customer_name,customer_email,commission_pct\n" +
                "49.99,Seeker plan · 12 months,Jane Smith,jane@example.com,10\n" +
                "119.99,Hunter plan · 12 months,John Doe,john@example.com,15\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "payment-links-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="agent-payment-links-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground overline">
            <Link2 className="w-4 h-4" /> Agent tools
          </div>
          <h1 className="text-3xl font-display font-light mt-1">Payment links</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Generate a shareable link for any customer. When they pay, funds land in Treasurescare treasury
            first; your commission auto-transfers to your wallet within seconds.
          </p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button size="lg" data-testid="create-payment-link-btn"><Plus className="w-4 h-4 mr-2" /> New link</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create payment link</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label>Amount (£)</Label>
                <Input type="number" step="0.01" value={form.amount_gbp} onChange={(e) => setForm(f => ({ ...f, amount_gbp: e.target.value }))} data-testid="link-amount" required />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Seeker Plan · 12 months" data-testid="link-description" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Customer name</Label>
                  <Input value={form.customer_name} onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="link-customer-name" />
                </div>
                <div>
                  <Label>Customer email</Label>
                  <Input type="email" value={form.customer_email} onChange={(e) => setForm(f => ({ ...f, customer_email: e.target.value }))} data-testid="link-customer-email" />
                </div>
              </div>
              <div>
                <Label>Your commission (%)</Label>
                <Input type="number" step="0.5" min={0} max={100} value={form.commission_pct} onChange={(e) => setForm(f => ({ ...f, commission_pct: e.target.value }))} data-testid="link-commission-pct" />
                <div className="text-xs text-muted-foreground mt-1">
                  You'll earn <b>{money((parseFloat(form.amount_gbp) || 0) * (parseFloat(form.commission_pct) || 0) / 100)}</b> when this link is paid.
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={creating} data-testid="link-create-submit">{creating ? "Creating…" : "Create link"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog open={showBulk} onOpenChange={(o) => { setShowBulk(o); if (!o) { setBulkResult(null); setCsvFile(null); } }}>
          <DialogTrigger asChild>
            <Button size="lg" variant="outline" data-testid="bulk-upload-btn"><Upload className="w-4 h-4 mr-2" /> Bulk CSV</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Bulk create payment links</DialogTitle></DialogHeader>
            {!bulkResult ? (
              <form onSubmit={uploadCsv} className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  Upload a CSV with columns <b>amount_gbp</b> and <b>description</b>. Optional: customer_name, customer_email, commission_pct. Max 500 rows.
                </div>
                <Button type="button" variant="link" size="sm" onClick={downloadCsvTemplate} className="p-0 h-auto" data-testid="download-template">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Download CSV template
                </Button>
                <div>
                  <Label>CSV file</Label>
                  <Input type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0])} data-testid="csv-file-input" required />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={uploading || !csvFile} data-testid="bulk-submit">
                    {uploading ? "Uploading…" : "Create all links"}
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              <div className="space-y-3">
                <div className="bg-emerald-500/10 text-emerald-500 rounded-lg p-3 text-sm" data-testid="bulk-result-success">
                  ✓ Created <b>{bulkResult.created}</b> payment links
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="bg-amber-500/10 text-amber-500 rounded-lg p-3 text-sm max-h-40 overflow-auto" data-testid="bulk-result-errors">
                    <div className="font-semibold mb-1">{bulkResult.errors.length} errors:</div>
                    {bulkResult.errors.slice(0, 20).map((e, i) => (
                      <div key={i} className="text-xs">Row {e.row}: {e.error}</div>
                    ))}
                  </div>
                )}
                <DialogFooter>
                  <Button onClick={() => { setBulkResult(null); setCsvFile(null); setShowBulk(false); }} data-testid="bulk-done-btn">Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Your payment links</CardTitle></CardHeader>
        <CardContent>
          {links.length === 0
            ? <div className="text-center py-8 text-muted-foreground text-sm">No payment links yet. Click "New link" to create your first.</div>
            : (
              <div className="divide-y divide-border">
                {links.map(l => {
                  const paid = l.status === "paid";
                  const commission = (l.amount_gbp * (l.commission_pct || 10) / 100);
                  return (
                    <div key={l.id} className="py-3 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`payment-link-row-${l.code}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {paid ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-muted-foreground" />}
                          <span className="font-medium truncate">{l.description}</span>
                          <Badge variant="outline" className="text-xs font-mono">{l.code}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {l.customer_name || l.customer_email || "no customer set"} · created {new Date(l.created_at).toLocaleDateString("en-GB")}
                          {paid && <> · paid {new Date(l.paid_at).toLocaleDateString("en-GB")}</>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <div className="font-mono font-semibold">{money(l.amount_gbp)}</div>
                          <div className="text-[10px] text-emerald-500 uppercase tracking-wide">+{money(commission)} to you</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => copyLink(l.code)} data-testid={`copy-link-${l.code}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" asChild data-testid={`open-link-${l.code}`}>
                          <a href={`/pay/${l.code}`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
